import type {
  ElementalType,
  ElementalTypeEnum,
  ElementalView,
} from '../types/elemental'

export type ElementalViewWithCounter = ElementalView & {
  count: number
  filterActive: boolean
  listCount: Map<string, number>
}

interface State {
  query: string
  viewsActives: Partial<
    Record<ElementalTypeEnum, ElementalViewWithCounter>
  > | null
  isAllActive: boolean
  scroll: number
  recentAccordionOpen: boolean
}

const setElementalList = (elementalTypeResponse: ElementalType[]) => {
  const listOptions = elementalTypeResponse.shift()
  const options = elementalTypeResponse.reduce((acc, item) => {
    if (!item.metadata.isActive)
      return acc

    acc[item.id] = {
      elementaltype: item.id,
      name: item.name,
      label: `${item.name}s`,
      url: `/${item.name}s`,
      count: 0,
      filterActive: true,
      listCount: new Map(),
    }

    return acc
  }, {} as Record<ElementalTypeEnum, ElementalViewWithCounter>)

  if (listOptions) {
    options[listOptions.id] = {
      elementaltype: listOptions.id,
      name: listOptions.name,
      label: `Combined ${listOptions.name}s`,
      url: `/${listOptions.name}s`,
      count: 0,
      filterActive: true,
      listCount: new Map(),

    }
  }

  return options
}

export const useElementalListsStore = defineStore('elementalList', () => {
  const init = () => {
    const types = useElementalTypes()

    const viewsActives = setElementalList(Array.from(types.value))

    return reactive({
      query: '',
      viewsActives,
      isAllActive: true,
      scroll: 0,
      recentAccordionOpen: true,
    })
  }
  const state: State = init()

  const toggleRecentAccordion = (value: boolean) => {
    state.recentAccordionOpen = value
  }

  const resetCounts = () => {
    state.query = ''
    if (!state.viewsActives)
      return
    Object.keys(state.viewsActives).forEach((key) => {
      const id = key as unknown as ElementalTypeEnum
      if (state.viewsActives?.[id])
        state.viewsActives[id].count = 0
    })
  }
  const setSearchQuery = (query: string) => {
    resetCounts()
    state.query = query
  }

  const countList = (id: string, type: ElementalTypeEnum, value: number) => {
    if (!state.viewsActives?.[type])
      return
    state.viewsActives[type].listCount.set(id, value)
  }

  const getAllActive = () => {
    if (!state.viewsActives) {
      state.isAllActive = false
      return
    }
    state.isAllActive
      = Object.keys(state.viewsActives).findIndex((key) => {
        const id = key as unknown as ElementalTypeEnum
        return state.viewsActives?.[id]?.filterActive === false
      }) === -1
  }

  const activeAll = () => {
    if (!state.viewsActives)
      return
    Object.keys(state.viewsActives).forEach((key) => {
      const id = key as unknown as ElementalTypeEnum
      if (state.viewsActives?.[id])
        state.viewsActives[id].filterActive = true
    })
    state.isAllActive = true
  }

  watch(
    () => state.query,
    (query) => {
      if (query.trim() === '')
        activeAll()
    },
  )

  const toggleFilter = (type: ElementalTypeEnum) => {
    if (!state.viewsActives?.[type])
      return
    if (state.isAllActive) {
      for (const key in state.viewsActives) {
        const id = key as unknown as ElementalTypeEnum
        // eslint-disable-next-line eqeqeq
        if (id == type)
          continue
        if (state.viewsActives?.[id])
          state.viewsActives[id].filterActive = false
      }
    }
    else {
      state.viewsActives[type].filterActive
        = !state.viewsActives[type].filterActive
    }
    getAllActive()
  }

  const updateScroll = (scroll: number) => {
    state.scroll = scroll
  }

  return {
    state,
    setSearchQuery,

    countList,
    activeAll,
    toggleFilter,
    getView: (elementalId: ElementalTypeEnum) => {
      return computed(() => {
        return state.viewsActives?.[elementalId] ?? null
      })
    },
    watchListCount: (elementalId: ElementalTypeEnum) => {
      return computed(() => {
        return state.viewsActives?.[elementalId] ?? null
      })
    },
    updateScroll,
    toggleRecentAccordion,
  }
})
